/* eslint-disable react-refresh/only-export-components -- components and their pure state resolver form one public primitive. */
import { Alert, Button, Empty, Result, Skeleton, Space } from 'antd';
import type { ReactNode } from 'react';

import { ApiError } from './api-client.js';

export type AsyncPageState =
  'loading' | 'initial-empty' | 'filtered-empty' | 'error' | 'forbidden' | 'ready';

export interface ResolvedPageState {
  readonly error?: unknown;
  readonly kind: AsyncPageState;
  readonly stale: boolean;
}

interface ResolvePageStateOptions<T> {
  readonly data: T | undefined;
  readonly error: unknown;
  readonly filtered?: boolean;
  readonly isEmpty: (data: T) => boolean;
  readonly pending: boolean;
}

export function resolvePageState<T>({
  data,
  error,
  filtered = false,
  isEmpty,
  pending,
}: ResolvePageStateOptions<T>): ResolvedPageState {
  if (error instanceof ApiError && error.status === 403) {
    return { error, kind: 'forbidden', stale: false };
  }
  if (data === undefined && pending) return { kind: 'loading', stale: false };
  if (data === undefined && error !== null && error !== undefined) {
    return { error, kind: 'error', stale: false };
  }
  if (data === undefined) return { kind: 'loading', stale: false };
  if (isEmpty(data)) {
    return {
      error: error ?? undefined,
      kind: filtered ? 'filtered-empty' : 'initial-empty',
      stale: error !== null && error !== undefined,
    };
  }
  return {
    error: error ?? undefined,
    kind: 'ready',
    stale: error !== null && error !== undefined,
  };
}

interface StateViewProps {
  readonly children: ReactNode;
  readonly emptyAction?: ReactNode;
  readonly emptyDescription?: string;
  readonly filteredDescription?: string;
  readonly onClearFilters?: () => void;
  readonly onRetry?: () => void;
  readonly onSafeBack?: () => void;
  readonly state: ResolvedPageState;
}

export function PageState(props: StateViewProps) {
  return <StateView {...props} loadingRows={4} />;
}

export function TableState(props: StateViewProps) {
  return <StateView {...props} loadingRows={3} />;
}

function StateView({
  children,
  emptyAction,
  emptyDescription = '暂无数据',
  filteredDescription = '当前筛选条件下没有结果',
  loadingRows,
  onClearFilters,
  onRetry,
  onSafeBack,
  state,
}: StateViewProps & { readonly loadingRows: number }) {
  if (state.kind === 'loading') {
    return <Skeleton active aria-label="正在加载" paragraph={{ rows: loadingRows }} />;
  }
  if (state.kind === 'forbidden') {
    return (
      <Result
        extra={
          <Button
            onClick={() => {
              if (onSafeBack) onSafeBack();
              else window.history.back();
            }}
          >
            安全返回
          </Button>
        }
        status="403"
        subTitle="你没有权限查看此内容。"
        title="无权访问"
      />
    );
  }
  if (state.kind === 'error') {
    return (
      <Result
        extra={onRetry ? <Button onClick={onRetry}>重试</Button> : undefined}
        status="error"
        subTitle={errorMessage(state.error)}
        title="加载失败"
      />
    );
  }
  if (state.kind === 'initial-empty') {
    return <Empty description={emptyDescription}>{emptyAction}</Empty>;
  }
  if (state.kind === 'filtered-empty') {
    return (
      <Empty description={filteredDescription}>
        {onClearFilters ? <Button onClick={onClearFilters}>清除筛选</Button> : undefined}
      </Empty>
    );
  }
  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      {state.stale ? (
        <Alert
          action={
            onRetry ? (
              <Button size="small" onClick={onRetry}>
                重试
              </Button>
            ) : undefined
          }
          description={errorMessage(state.error)}
          title="刷新失败，当前展示上次成功加载的数据"
          showIcon
          type="warning"
        />
      ) : null}
      {children}
    </Space>
  );
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : '请求失败，请稍后重试';
}
